import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import clsx from 'clsx';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

import * as actions from 'containers/App/actions';
import NCTask from './Checklist.Task';

import './nc-checklist.scss';

dayjs.extend(utc);

const SortableItem = (props) => {
  const { id, index } = props;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString({ ...transform, scaleX: 1, scaleY: 1 }),
    position: 'relative',
    zIndex: 100 - index,
    transition,
  };

  return (
    <NCTask
      {...props}
      attributes={attributes}
      listeners={listeners}
      setNodeRef={setNodeRef}
      className={clsx(isDragging && 'vertical-dnd-indicator')}
      style={style}
    />
  );
};

export const ChecklistComponent = ({
  tasks,
  module,
  active: activeIndex = -1,
  setActive = () => {},
  ...restProps
}) => {
  const dispatch = useDispatch();
  const { reorderTask } = bindActionCreators(actions, dispatch);
  const [taskList, setTaskList] = useState(tasks);

  useEffect(() => {
    setTaskList(tasks);
  }, [tasks]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 10 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const newIndex = taskList.findIndex(t => t.id === over.id);
      const oldIndex = taskList.findIndex(t => t.id === active.id);
      const variables = {
        taskId: active.id,
        referenceId: oldIndex > newIndex ? tasks[newIndex].id : tasks[newIndex + 1] ? tasks[newIndex + 1].id : null,
      };
      const params = { oldIndex, newIndex, module };
      reorderTask({ variables, params });
      setTaskList(oldItems => arrayMove(oldItems, oldIndex, newIndex));
    }
  };

  const setActiveIndex = (idx, code) => {
    if (code) {
      setActive(idx + 1);
    } else {
      setActive(-1);
    }
  };

  return (
    <div name="items">
      <DndContext
        sensors={sensors}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
        collisionDetection={closestCenter}
      >
        <SortableContext
          items={taskList}
          strategy={verticalListSortingStrategy}
        >
          {taskList?.map((task, idx) => (
            <SortableItem
              key={task.id}
              index={idx}
              module={module}
              referenceId={taskList.length > (idx + 1) ? taskList[idx + 1].id : null }
              active={activeIndex === idx}
              setActive={(code) => setActiveIndex(idx, code)}
              {...restProps}
              {...task}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
};
